import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { trackFeature } from '../utils/analytics';
import {
  UserIcon,
  BellIcon,
  ShieldCheckIcon,
  LinkIcon,
  CreditCardIcon,
  PencilSquareIcon,
  XMarkIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  StarIcon,
  PlusIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import {
  StarIcon as StarIconSolid,
  UserIcon as UserSolidIcon,
  BellIcon as BellSolidIcon,
  ShieldCheckIcon as ShieldSolidIcon,
  LinkIcon as LinkSolidIcon,
  CreditCardIcon as CreditCardSolidIcon,
  DocumentTextIcon as DocumentSolidIcon,
  RectangleStackIcon as RectangleStackSolidIcon,
  PencilSquareIcon as PencilSolidIcon,
  ArrowPathIcon as ArrowPathSolidIcon,
  XMarkIcon as XMarkSolidIcon,
  TrashIcon as TrashSolidIcon,
} from '@heroicons/react/24/solid';
import SectionTitle, { BRAND_SEGMENTS } from '../components/shared/SectionTitle';
import { PLATFORMS, loadPlatformDefinitions } from '../constants/platforms';
import { getPlatformTheme } from '../utils/platformTheme';
import { logout } from '../store/slices/authSlice';
import ProfilePhotoUpload from '../components/shared/ProfilePhotoUpload';
import PreOAuthConsentDialog from '../components/shared/PreOAuthConsentDialog';
import api from '../services/api';
import billingService from '../services/billingService';
import notificationService from '../services/notificationService';
import AddPaymentMethodForm from '../components/billing/AddPaymentMethodForm';
import toast from 'react-hot-toast';
import { format } from 'date-fns';


const SettingsPage = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: user?.name || user?.displayName || '',
    bio: user?.bio || '',
    location: user?.location || '',
    country: user?.country || '',
    timezone: user?.timezone || '',
    niche: user?.niche || '',
    website: user?.website || '',
    phone: user?.phone || '',
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Security form state
  const [securityForm, setSecurityForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingSecurity, setSavingSecurity] = useState(false);

  // Notification preferences (loaded from /api/v1/notifications/preferences)
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [notifLoading, setNotifLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Integrations state
  const [connections, setConnections] = useState([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  // Fresh avatar URLs keyed by connectionId. The stored avatarUrl (from /connections)
  // is a short-lived signed CDN URL for Facebook/TikTok that expires; the live
  // /connections/profiles endpoint returns a fresh one, so we overlay it here.
  const [freshAvatars, setFreshAvatars] = useState({});
  // URLs that failed to load — so we fall back to the branded platform icon
  // instead of the browser's broken-image glyph.
  const [failedAvatars, setFailedAvatars] = useState(() => new Set());
  const [editModal, setEditModal] = useState(null);       // connection object being edited
  const [connecting, setConnecting] = useState(null);     // platform key currently being redirected
  const [submitting, setSubmitting] = useState(false);
  const [bypassedPlatforms, setBypassedPlatforms] = useState(new Set());
  const [editingDisplayName, setEditingDisplayName] = useState(null); // connectionId being renamed
  const [displayNameInput, setDisplayNameInput] = useState('');
  const [platformDefs, setPlatformDefs] = useState(PLATFORMS); // API-loaded definitions (respects admin active flag)

  // Billing state
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showInvoices, setShowInvoices] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [changingPlan, setChangingPlan] = useState(null); // tier key being processed
  const [cancelingSubscription, setCancelingSubscription] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [addingPaymentMethod, setAddingPaymentMethod] = useState(false);
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const [removingPaymentMethod, setRemovingPaymentMethod] = useState(null);

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'billing', name: 'Billing', icon: CreditCardIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'integrations', name: 'Integrations', icon: LinkIcon },
  ];

  const loadConnections = async () => {
    setConnectionsLoading(true);
    try {
      const res = await api.get('/api/v1/platforms/connections');
      setConnections(res.data.data || []);
    } catch {
      // Non-fatal — show empty state
    } finally {
      setConnectionsLoading(false);
    }
    // Fetch fresh avatars in the background (live profile pull) so expired
    // stored CDN URLs get replaced. Non-blocking; failures are ignored.
    try {
      const pres = await api.get('/api/v1/platforms/connections/profiles');
      const map = {};
      (pres.data.data || []).forEach((p) => {
        if (p.connectionId && p.avatarUrl) map[p.connectionId] = p.avatarUrl;
      });
      if (Object.keys(map).length) setFreshAvatars((prev) => ({ ...prev, ...map }));
    } catch {
      // Non-fatal — fall back to stored avatar / platform icon
    }
  };

  const loadBillingData = async () => {
    setBillingLoading(true);
    try {
      const [sub, planList] = await Promise.all([
        billingService.getSubscription().catch(() => null),
        billingService.getPlans().catch(() => []),
      ]);
      setSubscription(sub);
      setPlans(planList);
    } catch {
      // Non-fatal
    } finally {
      setBillingLoading(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const data = await billingService.listInvoices();
      setInvoices(data || []);
    } catch {
      setInvoices([]);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const data = await billingService.listPaymentMethods();
      setPaymentMethods(data || []);
    } catch {
      setPaymentMethods([]);
    }
  };

  // Detect OAuth redirect-back result (?connected=YOUTUBE or ?oauthError=...)
  // Also detect Stripe checkout redirect (?checkout=success or ?checkout=canceled)
  // Load notification preferences when the tab is first opened
  useEffect(() => {
    if (activeTab !== 'notifications' || notifPrefs !== null) return;
    setNotifLoading(true);
    notificationService.getPreferences()
      .then(setNotifPrefs)
      .catch(() => toast.error('Failed to load notification preferences'))
      .finally(() => setNotifLoading(false));
  }, [activeTab]);

  // Optimistic toggle — revert on API failure
  const handleNotifToggle = async (field) => {
    const prev = notifPrefs;
    const next = { ...notifPrefs, [field]: !notifPrefs[field] };
    setNotifPrefs(next);
    try {
      await notificationService.updatePreferences({ [field]: next[field] });
    } catch {
      setNotifPrefs(prev);
      toast.error('Failed to save preference');
    }
  };

  const handleSendTestNotification = async () => {
    setSendingTest(true);
    try {
      await notificationService.sendTest();
      toast.success('Test notification sent — check the bell icon');
    } catch {
      toast.error('Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('connected');
    const oauthError = params.get('oauthError');
    const oauthCancelled = params.get('oauthCancelled');
    const tab = params.get('tab');
    const checkout = params.get('checkout');
    const setup = params.get('setup');

    if (setup === 'success') {
      setActiveTab('billing');
      setShowPaymentMethods(true);
      toast.success('Payment method added successfully!');
      loadPaymentMethods();
    } else if (checkout === 'success') {
      setActiveTab('billing');
      toast.success('Subscription activated successfully!');
      trackFeature('subscription_checkout_completed');
    } else if (checkout === 'canceled') {
      setActiveTab('billing');
      toast('Checkout canceled. No changes made.', { icon: 'i' });
    } else if (tab === 'billing') {
      setActiveTab('billing');
    } else if (connected || oauthError || oauthCancelled || tab === 'integrations') {
      setActiveTab('integrations');
      if (connected) {
        trackFeature('platform_oauth_completed');
        toast.success(`${connected} connected successfully!`);
      } else if (oauthError) {
        toast.error(`Connection failed: ${decodeURIComponent(oauthError)}`);
      } else if (oauthCancelled) {
        toast('Connection cancelled.', { icon: 'ℹ️' });
      }
    }
    // Clean URL params without reloading
    if (connected || oauthError || oauthCancelled || checkout || setup || tab) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'integrations') {
      loadConnections();
      // Load platform definitions from API (respects admin active/inactive flag)
      loadPlatformDefinitions().then(setPlatformDefs);
      // Fetch per-user platform overrides (Coming Soon bypasses granted by admin)
      api.get('/api/v1/platforms/overrides')
        .then((res) => setBypassedPlatforms(new Set(res.data?.data || [])))
        .catch(() => {}); // Non-fatal — default to no bypasses
    }
    if (activeTab === 'billing') {
      loadBillingData();
    }
  }, [activeTab]);

  // Group connections by platform for multi-account support
  const connectionsByPlatform = connections.reduce((acc, conn) => {
    if (!acc[conn.platform]) acc[conn.platform] = [];
    acc[conn.platform].push(conn);
    return acc;
  }, {});

  const platformsWithStatus = platformDefs.map((platform) => {
    const platformConnections = connectionsByPlatform[platform.value] || [];
    const connection = platformConnections.find((c) => c.primary) || platformConnections[0] || null;
    return { ...platform, connected: platformConnections.length > 0, connection, connections: platformConnections };
  });

  const totalSlots = connections.length;
  const userTier = user?.tier || 'FREE';
  const maxSlots = userTier === 'FREE' ? 2 : userTier === 'STARTER' ? 5 : Infinity;
  const canAddMore = totalSlots < maxSlots;

  // Pre-OAuth consent dialog state
  const [consentPlatform, setConsentPlatform] = useState(null);

  const handleConnect = (platform) => {
    // Show consent dialog before starting OAuth
    setConsentPlatform(platform.value);
  };

  const handleConsentAccepted = async () => {
    const platformValue = consentPlatform;
    setConsentPlatform(null);
    setConnecting(platformValue);
    try {
      const res = await api.get('/api/v1/platforms/oauth/authorize', {
        params: { platform: platformValue },
      });
      window.location.href = res.data.data.authorizationUrl;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start OAuth flow');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (connection, platformLabel) => {
    // Active → disconnect (soft, keeps history). Already inactive → remove the ghost.
    const isActive = connection.status === 'ACTIVE';
    const msg = isActive
      ? `Disconnect ${platformLabel}? This will remove your stored access token.`
      : `Remove this ${platformLabel} account? It's already disconnected and will be permanently removed.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/api/v1/platforms/disconnect/${connection.id}`);
      // Optimistically remove from local state so button flips immediately
      setConnections((prev) => prev.filter((c) => c.id !== connection.id));
      trackFeature('platform_disconnect');
      toast.success(isActive ? `${platformLabel} disconnected` : `${platformLabel} account removed`);
      // Then sync from server to confirm
      loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update platform');
    }
  };

  const handleRefreshToken = async (connection) => {
    setSubmitting(true);
    try {
      await api.post(`/api/v1/platforms/refresh/${connection.id}`);
      toast.success('Token refreshed successfully');
      setEditModal(null);
      await loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to refresh token');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetPrimary = async (connectionId) => {
    try {
      await api.put(`/api/v1/platforms/connections/${connectionId}/primary`);
      toast.success('Primary account updated');
      await loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set primary');
    }
  };

  const handleSaveDisplayName = async (connectionId) => {
    if (!displayNameInput.trim()) return;
    try {
      await api.patch(`/api/v1/platforms/connections/${connectionId}/display-name`, null, {
        params: { displayName: displayNameInput.trim() },
      });
      toast.success('Display name updated');
      setEditingDisplayName(null);
      await loadConnections();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update display name');
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.put('/api/v1/auth/profile', {
        displayName: profileForm.name,
        bio: profileForm.bio || undefined,
        location: profileForm.location || undefined,
        country: profileForm.country || undefined,
        timezone: profileForm.timezone || undefined,
        niche: profileForm.niche || undefined,
        website: profileForm.website || undefined,
        phone: profileForm.phone || undefined,
      });
      trackFeature('profile_update');
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (securityForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSavingSecurity(true);
    try {
      await api.put('/api/v1/auth/password', {
        currentPassword: securityForm.currentPassword,
        newPassword: securityForm.newPassword,
      });
      trackFeature('security_change_password');
      toast.success('Password updated successfully');
      setSecurityForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally {
      setSavingSecurity(false);
    }
  };

  // ── Billing Handlers ───────────────────────────────────────────────────

  const TIER_ORDER = ['FREE', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'];

  const handlePlanAction = async (targetTier) => {
    const currentTier = subscription?.tier || 'FREE';
    if (targetTier === currentTier) return;

    const currentIdx = TIER_ORDER.indexOf(currentTier);
    const targetIdx = TIER_ORDER.indexOf(targetTier);
    const isUpgrade = targetIdx > currentIdx;

    // FREE → PAID: use Stripe Checkout
    if (currentTier === 'FREE' && targetTier !== 'FREE') {
      setChangingPlan(targetTier);
      try {
        const baseUrl = `${window.location.origin}/dashboard/settings`;
        const result = await billingService.createCheckoutSession({
          tier: targetTier,
          successUrl: `${baseUrl}?checkout=success`,
          cancelUrl: `${baseUrl}?checkout=canceled`,
          billingInterval: billingCycle,
        });
        window.location.href = result.url;
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to start checkout');
        setChangingPlan(null);
      }
      return;
    }

    // PAID → FREE: cancel subscription
    if (targetTier === 'FREE') {
      setShowCancelConfirm(true);
      return;
    }

    // No live Stripe subscription behind this tier (e.g. an admin-granted tier
    // or data migrated from Stripe test mode) — there's nothing to prorate, so
    // send the user through Checkout to establish a real paid subscription.
    if (!subscription?.stripeSubscriptionId) {
      setChangingPlan(targetTier);
      try {
        const baseUrl = `${window.location.origin}/dashboard/settings`;
        const result = await billingService.createCheckoutSession({
          tier: targetTier,
          successUrl: `${baseUrl}?checkout=success`,
          cancelUrl: `${baseUrl}?checkout=canceled`,
          billingInterval: billingCycle,
        });
        window.location.href = result.url;
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to start checkout');
        setChangingPlan(null);
      }
      return;
    }

    // PAID → PAID: change plan with proration
    const action = isUpgrade ? 'upgrade' : 'downgrade';
    const targetPlan = plans.find((p) => p.tier === targetTier);
    if (!window.confirm(`${isUpgrade ? 'Upgrade' : 'Downgrade'} to ${targetPlan?.name || targetTier}? ${isUpgrade ? 'You will be charged a prorated amount.' : 'Your billing will be adjusted at the next cycle.'}`)) return;

    setChangingPlan(targetTier);
    try {
      const result = await billingService.changePlan(targetTier);
      setSubscription(result);
      trackFeature(`subscription_${action}`);
      toast.success(`Plan ${action}d to ${targetPlan?.name || targetTier}!`);
      loadBillingData();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${action} plan`);
    } finally {
      setChangingPlan(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelingSubscription(true);
    try {
      const result = await billingService.cancelSubscription(true);
      setSubscription(result);
      setShowCancelConfirm(false);
      trackFeature('subscription_canceled');
      toast.success('Subscription will be canceled at the end of your billing period.');
      loadBillingData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setCancelingSubscription(false);
    }
  };

  const handleReactivate = async () => {
    try {
      const result = await billingService.reactivateSubscription();
      setSubscription(result);
      trackFeature('subscription_reactivated');
      toast.success('Subscription reactivated!');
      loadBillingData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reactivate subscription');
    }
  };

  const handleManageBilling = async () => {
    try {
      const returnUrl = `${window.location.origin}/dashboard/settings?tab=billing`;
      const result = await billingService.createPortalSession(returnUrl);
      window.location.href = result.url;
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to open billing portal');
    }
  };

  // Stripe's customer portal is only useful once the user has a paid plan —
  // free-tier users would land on an empty portal, and their invoices (if any)
  // are already listed in-app below.
  const showManageOnStripe = Boolean(subscription?.stripeCustomerId) && subscription?.tier !== 'FREE';

  const handleAddPaymentMethod = async () => {
    if (subscription?.stripeCustomerId) {
      // Existing Stripe customer — create a SetupIntent and show inline form
      setAddingPaymentMethod(true);
      try {
        const result = await billingService.createSetupIntent();
        setSetupClientSecret(result.clientSecret);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to initialize payment setup');
      } finally {
        setAddingPaymentMethod(false);
      }
    } else {
      // No Stripe customer yet — redirect to checkout to create subscription first
      setAddingPaymentMethod(true);
      try {
        const result = await billingService.createCheckoutSession({
          tier: 'STARTER',
          successUrl: `${window.location.origin}/dashboard/settings?tab=billing&setup=success`,
          cancelUrl: `${window.location.origin}/dashboard/settings?tab=billing`,
          billingInterval: 'monthly',
        });
        if (result?.url) {
          window.location.href = result.url;
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to start checkout');
      } finally {
        setAddingPaymentMethod(false);
      }
    }
  };

  const handlePaymentMethodAdded = () => {
    setSetupClientSecret(null);
    loadPaymentMethods();
  };

  const handleRemovePaymentMethod = async (paymentMethodId) => {
    setRemovingPaymentMethod(paymentMethodId);
    try {
      await billingService.deletePaymentMethod(paymentMethodId);
      toast.success('Payment method removed');
      setPaymentMethods((prev) => prev.filter((pm) => pm.id !== paymentMethodId));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove payment method');
    } finally {
      setRemovingPaymentMethod(null);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/api/v1/auth/account', { params: { reason: 'User requested deletion' } });
      toast.success('Your account has been deleted.');
      setShowDeleteModal(false);
      dispatch(logout());
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Settings</h2>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Tabs */}
        <div className="lg:col-span-1">
          <div className="card p-2 flex gap-1 overflow-x-auto lg:block lg:overflow-visible">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 w-auto lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white shadow-md'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-800'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          {activeTab === 'profile' && (
            <div className="card p-6 space-y-6">
              <SectionTitle icon={UserSolidIcon} gradient={BRAND_SEGMENTS.orangePink} className="mb-0">
                Profile Information
              </SectionTitle>

              <div className="flex justify-center py-6 border-b border-gray-200 dark:border-dark-700">
                <ProfilePhotoUpload
                  currentPhoto={user?.avatarUrl}
                  onUpload={async (formData) => {
                    // Avatar upload endpoint not yet available — profile update used for URL-based avatars
                    toast('Photo upload is not available yet. Use a URL-based avatar via profile settings.', { icon: 'ℹ️' });
                  }}
                  userName={user?.name || user?.displayName || 'User'}
                />
              </div>

              <form onSubmit={handleSaveProfile} className="space-y-4">
                <div>
                  <label className="label">Full Name</label>
                  <input
                    type="text"
                    className="input"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm(f => ({ ...f, name: e.target.value }))}
                    disabled={savingProfile}
                  />
                </div>
                <div>
                  <label className="label">Email</label>
                  <input type="email" className="input" defaultValue={user?.email} disabled />
                </div>
                <div>
                  <label className="label">Bio</label>
                  <textarea
                    className="input"
                    rows="3"
                    placeholder="Tell us about yourself..."
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm(f => ({ ...f, bio: e.target.value }))}
                    disabled={savingProfile}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="label">City / Location</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="e.g. San Francisco"
                      value={profileForm.location}
                      onChange={(e) => setProfileForm(f => ({ ...f, location: e.target.value }))}
                      disabled={savingProfile}
                    />
                  </div>
                  <div>
                    <label className="label">Country</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="e.g. United States"
                      value={profileForm.country}
                      onChange={(e) => setProfileForm(f => ({ ...f, country: e.target.value }))}
                      disabled={savingProfile}
                    />
                  </div>
                  <div>
                    <label className="label">Timezone</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="e.g. America/Los_Angeles"
                      value={profileForm.timezone}
                      onChange={(e) => setProfileForm(f => ({ ...f, timezone: e.target.value }))}
                      disabled={savingProfile}
                    />
                  </div>
                  <div>
                    <label className="label">Content Niche</label>
                    <input
                      type="text"
                      className="input"
                      placeholder="e.g. Gaming, Beauty, Tech"
                      value={profileForm.niche}
                      onChange={(e) => setProfileForm(f => ({ ...f, niche: e.target.value }))}
                      disabled={savingProfile}
                    />
                  </div>
                  <div>
                    <label className="label">Website</label>
                    <input
                      type="url"
                      className="input"
                      placeholder="https://yoursite.com"
                      value={profileForm.website}
                      onChange={(e) => setProfileForm(f => ({ ...f, website: e.target.value }))}
                      disabled={savingProfile}
                    />
                  </div>
                  <div>
                    <label className="label">Phone</label>
                    <input
                      type="tel"
                      className="input"
                      placeholder="+1 555 000 0000"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                      disabled={savingProfile}
                    />
                  </div>
                </div>

                <button type="submit" className="btn text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md" disabled={savingProfile}>
                  {savingProfile ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="card p-6 space-y-6">
              <SectionTitle
                icon={BellSolidIcon}
                gradient={BRAND_SEGMENTS.pinkFuchsia}
                className="mb-0"
                right={(
                  <button
                    onClick={handleSendTestNotification}
                    disabled={sendingTest}
                    className="text-xs font-medium text-violet-600 dark:text-violet-400 hover:underline disabled:opacity-50"
                  >
                    {sendingTest ? 'Sending…' : 'Send test notification'}
                  </button>
                )}
              >
                Notification Preferences
              </SectionTitle>

              {notifLoading || !notifPrefs ? (
                <div className="flex items-center justify-center py-10">
                  <div className="spinner" style={{ color: '#a855f7' }} />
                </div>
              ) : (
                <div className="space-y-1">
                  {[
                    { field: 'emailEnabled',    label: 'Email Notifications',  description: 'Receive email updates about your account' },
                    { field: 'pushEnabled',     label: 'In-App Notifications', description: 'Real-time notifications inside the dashboard' },
                    { field: 'healthAlerts',    label: 'Health Alerts',        description: 'Get notified when anomalies are detected' },
                    { field: 'revenueAlerts',   label: 'Revenue Updates',      description: 'Revenue reports and reconciliation status' },
                    { field: 'dealsAlerts',     label: 'Deal Alerts',          description: 'Updates on brand deal stages and deliverables' },
                    { field: 'marketingEmails', label: 'Product & Marketing',  description: 'Occasional news, tips, and feature announcements' },
                  ].map((item) => (
                    <div key={item.field} className="flex items-center justify-between py-3 border-b border-gray-200 dark:border-dark-700 last:border-0">
                      <div className="pr-4">
                        <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{item.description}</p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={!!notifPrefs[item.field]}
                        aria-label={item.label}
                        onClick={() => handleNotifToggle(item.field)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                          notifPrefs[item.field]
                            ? 'bg-gradient-to-r from-pink-500 to-violet-600'
                            : 'bg-gray-200 dark:bg-dark-700'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          notifPrefs[item.field] ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'security' && (
            <div className="card p-6 space-y-6">
              <SectionTitle icon={ShieldSolidIcon} gradient={BRAND_SEGMENTS.fuchsiaViolet} className="mb-0">
                Security Settings
              </SectionTitle>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="label">Current Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="••••••••"
                    value={securityForm.currentPassword}
                    onChange={(e) => setSecurityForm(f => ({ ...f, currentPassword: e.target.value }))}
                    disabled={savingSecurity}
                    required
                  />
                </div>
                <div>
                  <label className="label">New Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="Min. 8 characters"
                    value={securityForm.newPassword}
                    onChange={(e) => setSecurityForm(f => ({ ...f, newPassword: e.target.value }))}
                    disabled={savingSecurity}
                    required
                  />
                </div>
                <div>
                  <label className="label">Confirm New Password</label>
                  <input
                    type="password"
                    className="input"
                    placeholder="••••••••"
                    value={securityForm.confirmPassword}
                    onChange={(e) => setSecurityForm(f => ({ ...f, confirmPassword: e.target.value }))}
                    disabled={savingSecurity}
                    required
                  />
                  {securityForm.confirmPassword && securityForm.newPassword !== securityForm.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>
                <button
                  type="submit"
                  className="btn text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
                  disabled={savingSecurity || !securityForm.currentPassword || !securityForm.newPassword}
                >
                  {savingSecurity ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'integrations' && (
            <div className="card p-6 space-y-6">
              <SectionTitle
                icon={LinkSolidIcon}
                gradient={BRAND_SEGMENTS.violetBlue}
                className="mb-0"
                right={maxSlots !== Infinity ? (
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {totalSlots}/{maxSlots} slots used
                    {userTier === 'FREE' && (
                      <span className="ml-1 text-xs text-amber-600 dark:text-amber-400">(Upgrade for more)</span>
                    )}
                  </span>
                ) : null}
              >
                Platform Integrations
              </SectionTitle>

              {connectionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="spinner" style={{ color: '#a855f7' }} />
                  <span className="ml-3 text-gray-500">Loading connections...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  {platformsWithStatus.map((platform) => {
                    const PlatformIcon = platform.icon;
                    const isComingSoon = platform.comingSoon && !bypassedPlatforms.has(platform.value);
                    const platConns = platform.connections || [];
                    const theme = getPlatformTheme(platform.value, platform.brandColor);
                    const isConnected = platConns.length > 0;
                    return (
                      <div
                        key={platform.value}
                        className="relative overflow-hidden border rounded-xl transition-shadow hover:shadow-md"
                        style={{
                          borderColor: `${theme.tint}${isConnected ? '4d' : '26'}`,
                          backgroundImage: `linear-gradient(180deg, ${theme.tint}0f, transparent 46%)`,
                        }}
                      >
                        {/* Platform brand accent bar */}
                        <div className="absolute top-0 inset-x-0 h-1" style={{ background: theme.bar }} />

                        {isComingSoon && (
                          <div className="absolute inset-0 bg-white/70 dark:bg-dark-900/70 backdrop-blur-[2px] z-10 flex flex-col items-center justify-center rounded-lg">
                            <PlatformIcon className="text-3xl text-gray-400 dark:text-gray-500 mb-2" />
                            <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">Coming Soon</span>
                          </div>
                        )}

                        {/* Platform header */}
                        <div className="flex flex-wrap items-center justify-between gap-3 p-4 pt-5 border-b border-gray-100 dark:border-dark-700">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
                              style={{ background: theme.badge, boxShadow: `0 6px 16px -8px ${theme.tint}` }}
                            >
                              <PlatformIcon className="text-xl text-white" />
                            </div>
                            <div>
                              <span className="font-medium block">{platform.label}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {platConns.length === 0
                                  ? 'No accounts connected'
                                  : `${platConns.length} account${platConns.length > 1 ? 's' : ''} connected`}
                              </span>
                            </div>
                          </div>
                          {!isComingSoon && (
                            <button
                              onClick={() => handleConnect(platform)}
                              disabled={connecting === platform.value || (!canAddMore && platConns.length > 0)}
                              className="btn btn-sm flex items-center gap-1.5 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
                              title={!canAddMore && platConns.length > 0 ? 'Upgrade to add more accounts' : `Connect ${platform.label}`}
                            >
                              <PlusIcon className="w-4 h-4" />
                              {platConns.length === 0 ? 'Connect' : 'Add Account'}
                            </button>
                          )}
                        </div>

                        {/* Connected accounts list */}
                        {platConns.length > 0 && (
                          <div className="divide-y divide-gray-100 dark:divide-dark-700">
                            {platConns.map((conn) => (
                              <div key={conn.id} className="flex flex-wrap items-center justify-between gap-y-2 p-4 hover:bg-gray-50 dark:hover:bg-dark-800/50 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                  {(() => {
                                    const avatarSrc = freshAvatars[conn.id] || conn.avatarUrl;
                                    return avatarSrc && !failedAvatars.has(avatarSrc);
                                  })() ? (
                                    <img
                                      src={freshAvatars[conn.id] || conn.avatarUrl}
                                      alt=""
                                      className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                      style={{ boxShadow: `0 0 0 2px ${theme.tint}40` }}
                                      onError={() => {
                                        const failed = freshAvatars[conn.id] || conn.avatarUrl;
                                        setFailedAvatars((prev) => new Set(prev).add(failed));
                                      }}
                                    />
                                  ) : (
                                    <div
                                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{ background: theme.badge }}
                                    >
                                      <PlatformIcon className="text-sm text-white" />
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    {editingDisplayName === conn.id ? (
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="text"
                                          className="input input-sm text-sm py-0.5 px-2 w-40"
                                          value={displayNameInput}
                                          onChange={(e) => setDisplayNameInput(e.target.value)}
                                          onKeyDown={(e) => e.key === 'Enter' && handleSaveDisplayName(conn.id)}
                                          autoFocus
                                        />
                                        <button onClick={() => handleSaveDisplayName(conn.id)} className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded">
                                          <CheckIcon className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => setEditingDisplayName(null)} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700 rounded">
                                          <XMarkIcon className="w-3.5 h-3.5" />
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="font-medium text-sm block truncate">
                                        {conn.displayName || conn.platformUsername || conn.platformAccountId}
                                      </span>
                                    )}
                                    {conn.platformUsername && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate block">
                                        @{conn.platformUsername}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {/* Primary badge / set primary button */}
                                  {platConns.length > 1 && (
                                    conn.primary ? (
                                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                                        <StarIconSolid className="w-3 h-3" />
                                        Primary
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handleSetPrimary(conn.id)}
                                        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 px-2 py-0.5 rounded-full hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
                                        title="Set as primary"
                                      >
                                        <StarIcon className="w-3 h-3" />
                                        Set Primary
                                      </button>
                                    )
                                  )}

                                  <span className={`badge text-xs ${conn.status === 'ACTIVE' ? 'badge-success' : 'badge-warning'}`}>
                                    {conn.status === 'ACTIVE' ? 'Active' : conn.status}
                                  </span>

                                  {/* Edit display name */}
                                  <button
                                    onClick={() => { setEditingDisplayName(conn.id); setDisplayNameInput(conn.displayName || ''); }}
                                    className="p-2 rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-200 hover:border-violet-400 hover:text-violet-600 dark:hover:border-violet-500 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                                    title="Edit display name"
                                  >
                                    <PencilSolidIcon className="w-4 h-4" />
                                  </button>

                                  {/* Manage connection */}
                                  <button
                                    onClick={() => setEditModal(conn)}
                                    className="p-2 rounded-lg border border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 text-gray-700 dark:text-gray-200 hover:border-violet-400 hover:text-violet-600 dark:hover:border-violet-500 dark:hover:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
                                    title="Manage connection"
                                  >
                                    <ArrowPathSolidIcon className="w-4 h-4" />
                                  </button>

                                  {/* Disconnect (active) or Remove (already inactive) */}
                                  <button
                                    onClick={() => handleDisconnect(conn, platform.label)}
                                    className="p-2 rounded-lg border border-red-300 dark:border-red-900/50 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 hover:border-red-400 transition-colors"
                                    title={conn.status === 'ACTIVE' ? 'Disconnect' : 'Remove account'}
                                  >
                                    {conn.status === 'ACTIVE'
                                      ? <XMarkSolidIcon className="w-4 h-4" />
                                      : <TrashSolidIcon className="w-4 h-4" />}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Empty state — no connections for this platform */}
                        {platConns.length === 0 && !isComingSoon && (
                          <div className="p-4 text-center text-sm text-gray-400 dark:text-gray-500">
                            Connect your {platform.label} account to start syncing data
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="space-y-6">
              {billingLoading ? (
                <div className="card p-6 flex items-center justify-center py-12">
                  <div className="spinner" style={{ color: '#a855f7' }} />
                  <span className="ml-3 text-gray-500">Loading billing info...</span>
                </div>
              ) : (
                <>
                  {/* Section A: Current Plan */}
                  <div className="card p-6">
                    <SectionTitle
                      icon={CreditCardSolidIcon}
                      gradient={BRAND_SEGMENTS.orangePink}
                      className="mb-4"
                      right={showManageOnStripe ? (
                        <button onClick={handleManageBilling} className="text-sm text-violet-600 hover:text-violet-700 dark:text-violet-400 font-medium">
                          Manage on Stripe
                        </button>
                      ) : null}
                    >
                      Current Plan
                    </SectionTitle>

                    <div className="flex items-center gap-4">
                      <div className={`px-4 py-2 rounded-lg font-bold text-lg ${
                        subscription?.tier === 'FREE' ? 'bg-gray-100 dark:bg-dark-800 text-gray-700 dark:text-gray-300' :
                        subscription?.tier === 'STARTER' ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400' :
                        subscription?.tier === 'PROFESSIONAL' ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400' :
                        'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                      }`}>
                        {subscription?.tier === 'PROFESSIONAL' ? 'Professional' : subscription?.tier?.charAt(0) + subscription?.tier?.slice(1).toLowerCase() || 'Free'}
                      </div>
                      <div>
                        <span className={`badge text-xs ${
                          subscription?.status === 'ACTIVE' ? 'badge-success' :
                          subscription?.status === 'PAST_DUE' ? 'badge-warning' :
                          subscription?.status === 'CANCELED' ? 'badge-danger' : 'badge-info'
                        }`}>
                          {subscription?.status || 'ACTIVE'}
                        </span>
                      </div>
                    </div>

                    {subscription?.currentPeriodEnd && subscription?.tier !== 'FREE' && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                        {subscription?.status === 'CANCELED'
                          ? `Access until ${format(new Date(subscription.currentPeriodEnd), 'MMMM dd, yyyy')}`
                          : `Next billing date: ${format(new Date(subscription.currentPeriodEnd), 'MMMM dd, yyyy')}`}
                      </p>
                    )}

                    {subscription?.tier !== 'FREE' && subscription?.status === 'ACTIVE' && (
                      <button
                        onClick={() => setShowCancelConfirm(true)}
                        className="mt-4 text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                      >
                        Cancel Subscription
                      </button>
                    )}
                    {subscription?.status === 'CANCELED' && subscription?.tier !== 'FREE' && (
                      <button
                        onClick={handleReactivate}
                        className="mt-4 btn btn-sm text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
                      >
                        Reactivate Subscription
                      </button>
                    )}
                  </div>

                  {/* Section B: Plan Comparison */}
                  <div className="card p-6">
                    <SectionTitle
                      icon={RectangleStackSolidIcon}
                      gradient={BRAND_SEGMENTS.pinkFuchsia}
                      className="mb-6"
                      right={<div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
                        <span className={`text-sm font-medium ${billingCycle === 'monthly' ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                          Monthly
                        </span>
                        <button
                          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            billingCycle === 'yearly' ? 'bg-gradient-to-r from-pink-500 to-violet-600' : 'bg-gray-200 dark:bg-dark-700'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                        <span className={`text-sm font-medium ${billingCycle === 'yearly' ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                          Yearly
                        </span>
                        {billingCycle === 'yearly' && (
                          <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                            Save ~17%
                          </span>
                        )}
                      </div>}
                    >
                      Available Plans
                    </SectionTitle>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {plans.map((plan) => {
                        const isCurrent = plan.tier === (subscription?.tier || 'FREE');
                        const currentIdx = TIER_ORDER.indexOf(subscription?.tier || 'FREE');
                        const planIdx = TIER_ORDER.indexOf(plan.tier);
                        const isUpgrade = planIdx > currentIdx;
                        const isDowngrade = planIdx < currentIdx;
                        const price = billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;
                        const isPopular = plan.tier === 'PROFESSIONAL';

                        return (
                          <div
                            key={plan.tier}
                            className={`relative rounded-xl border-2 p-5 transition-all flex flex-col ${
                              isCurrent ? 'border-violet-500 bg-violet-50/50 dark:bg-violet-900/10' :
                              isPopular ? 'border-violet-300 dark:border-violet-700' :
                              'border-gray-200 dark:border-dark-700'
                            }`}
                          >
                            {isPopular && !isCurrent && (
                              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-gradient-to-r from-pink-500 to-violet-600 text-white text-xs font-semibold rounded-full whitespace-nowrap shadow-md">
                                Most Popular
                              </span>
                            )}
                            {isCurrent && (
                              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-green-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                                Current Plan
                              </span>
                            )}

                            <h4 className="text-lg font-bold text-gray-900 dark:text-white">{plan.name}</h4>
                            <div className="mt-2 mb-4">
                              {price > 0 ? (
                                <div className="flex items-baseline">
                                  <span className="text-3xl font-bold text-gray-900 dark:text-white">${price}</span>
                                  <span className="text-gray-500 dark:text-gray-400 ml-1">/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
                                </div>
                              ) : plan.tier === 'ENTERPRISE' ? (
                                <span className="text-3xl font-bold text-gray-900 dark:text-white">Custom</span>
                              ) : (
                                <span className="text-3xl font-bold text-gray-900 dark:text-white">Free</span>
                              )}
                            </div>

                            <ul className="space-y-2 flex-1">
                              {plan.features?.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  {f}
                                </li>
                              ))}
                            </ul>

                            <div className="mt-5">
                            {isCurrent ? (
                              <div className="w-full py-2 text-center text-sm font-medium text-gray-400 bg-gray-100 dark:bg-dark-800 rounded-lg">
                                Current Plan
                              </div>
                            ) : plan.tier === 'ENTERPRISE' ? (
                              <a
                                href="mailto:support@omniconnx.com?subject=Enterprise Plan Inquiry"
                                className="block w-full py-2 text-center text-sm font-semibold bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-lg hover:opacity-90 transition-opacity"
                              >
                                Contact Sales
                              </a>
                            ) : (
                              <button
                                onClick={() => handlePlanAction(plan.tier)}
                                disabled={changingPlan !== null}
                                className={`w-full py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                                  isUpgrade
                                    ? 'bg-gradient-to-r from-pink-500 to-violet-600 text-white hover:from-pink-600 hover:to-violet-700 shadow-md'
                                    : 'bg-gray-200 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-dark-600'
                                }`}
                              >
                                {changingPlan === plan.tier ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                    Processing...
                                  </span>
                                ) : isUpgrade ? 'Upgrade' : 'Downgrade'}
                              </button>
                            )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Section C: Invoice History (collapsible) */}
                  <div className="card">
                    <button
                      onClick={() => { setShowInvoices(!showInvoices); if (!showInvoices && invoices.length === 0) loadInvoices(); }}
                      className="w-full flex items-center justify-between p-6"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center shadow-sm flex-shrink-0">
                          <DocumentSolidIcon className="w-3.5 h-3.5 text-white" />
                        </span>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-white tracking-tight">Invoice History</h3>
                      </span>
                      {showInvoices ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
                    </button>
                    {showInvoices && (
                      <div className="px-6 pb-6">
                        {invoices.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">No invoices yet.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-200 dark:border-dark-700">
                                  <th className="text-left py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Date</th>
                                  <th className="text-left py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Amount</th>
                                  <th className="text-left py-2 px-3 font-medium text-gray-500 dark:text-gray-400">Status</th>
                                  <th className="text-right py-2 px-3 font-medium text-gray-500 dark:text-gray-400">PDF</th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoices.map((inv) => (
                                  <tr key={inv.id} className="border-b border-gray-100 dark:border-dark-800">
                                    <td className="py-2 px-3 text-gray-900 dark:text-white">{inv.createdAt ? format(new Date(inv.createdAt), 'MMM dd, yyyy') : '—'}</td>
                                    <td className="py-2 px-3 text-gray-900 dark:text-white">${inv.amount?.toFixed(2)} {inv.currency}</td>
                                    <td className="py-2 px-3">
                                      <span className={`badge text-xs ${inv.status === 'paid' ? 'badge-success' : inv.status === 'open' ? 'badge-warning' : 'badge-danger'}`}>
                                        {inv.status}
                                      </span>
                                    </td>
                                    <td className="py-2 px-3 text-right">
                                      {inv.pdfUrl && (
                                        <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:text-violet-700 dark:text-violet-400 font-medium">
                                          Download
                                        </a>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Section D: Payment Methods (collapsible) */}
                  <div className="card">
                    <button
                      onClick={() => { setShowPaymentMethods(!showPaymentMethods); if (!showPaymentMethods && paymentMethods.length === 0) loadPaymentMethods(); }}
                      className="w-full flex items-center justify-between p-6"
                    >
                      <span className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center shadow-sm flex-shrink-0">
                          <CreditCardSolidIcon className="w-3.5 h-3.5 text-white" />
                        </span>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-white tracking-tight">Payment Methods</h3>
                      </span>
                      {showPaymentMethods ? <ChevronUpIcon className="w-5 h-5 text-gray-400" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" />}
                    </button>
                    {showPaymentMethods && (
                      <div className="px-6 pb-6">
                        {/* Accepted payment types banner */}
                        <div className="mb-4 p-3 bg-gray-50 dark:bg-dark-800 rounded-lg">
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 font-medium">Accepted Payment Methods</p>
                          <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <CreditCardIcon className="w-5 h-5 text-blue-600" />
                              <span className="text-xs text-gray-700 dark:text-gray-300">Credit / Debit Card</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="w-5 h-5 text-[#003087]" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .76-.654h6.197c2.053 0 3.479.43 4.235 1.278.356.399.592.838.703 1.304.115.488.117 1.074.006 1.794l-.013.07v.623l.486.275c.41.207.738.444.985.713.372.405.614.912.718 1.506.108.611.072 1.338-.106 2.16-.204.945-.539 1.768-.996 2.442-.42.618-.966 1.128-1.622 1.513-.632.371-1.379.647-2.22.819-.813.168-1.739.253-2.751.253H10.69a.952.952 0 0 0-.94.803l-.038.197-.635 4.024-.029.143a.952.952 0 0 1-.94.803H7.076z"/></svg>
                              <span className="text-xs text-gray-700 dark:text-gray-300">PayPal</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z" fill="#4285F4"/></svg>
                              <span className="text-xs text-gray-700 dark:text-gray-300">Google Pay</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                              <span className="text-xs text-gray-700 dark:text-gray-300">Apple Pay</span>
                            </div>
                          </div>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                            All payments processed securely by Stripe (PCI DSS Level 1). Card data is never stored on our servers.
                          </p>
                        </div>

                        {paymentMethods.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400">No payment methods on file.</p>
                        ) : (
                          <div className="space-y-3">
                            {paymentMethods.map((pm) => (
                              <div key={pm.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-dark-700 rounded-lg">
                                <div className="flex items-center gap-3">
                                  {pm.type === 'paypal' ? (
                                    <svg className="w-6 h-6 text-[#003087]" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.771.771 0 0 1 .76-.654h6.197c2.053 0 3.479.43 4.235 1.278.356.399.592.838.703 1.304.115.488.117 1.074.006 1.794l-.013.07v.623l.486.275c.41.207.738.444.985.713.372.405.614.912.718 1.506.108.611.072 1.338-.106 2.16-.204.945-.539 1.768-.996 2.442-.42.618-.966 1.128-1.622 1.513-.632.371-1.379.647-2.22.819-.813.168-1.739.253-2.751.253H10.69a.952.952 0 0 0-.94.803l-.038.197-.635 4.024-.029.143a.952.952 0 0 1-.94.803H7.076z"/></svg>
                                  ) : pm.type === 'link' ? (
                                    <svg className="w-6 h-6 text-purple-500" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
                                  ) : (
                                    <CreditCardIcon className="w-6 h-6 text-gray-400" />
                                  )}
                                  <div>
                                    <span className="font-medium text-sm text-gray-900 dark:text-white capitalize">{pm.brand || pm.type}</span>
                                    {pm.type === 'card' && pm.last4 && (
                                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">**** {pm.last4}</span>
                                    )}
                                    {(pm.type === 'paypal' || pm.type === 'link') && pm.email && (
                                      <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">{pm.email}</span>
                                    )}
                                    {pm.walletType && pm.type === 'card' && (
                                      <span className="ml-2 text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                        {pm.walletType === 'google_pay' ? 'Google Pay' : pm.walletType === 'apple_pay' ? 'Apple Pay' : pm.walletType}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  {pm.type === 'card' && pm.expMonth && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      Exp {pm.expMonth}/{pm.expYear}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleRemovePaymentMethod(pm.id)}
                                    disabled={removingPaymentMethod === pm.id}
                                    className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded transition-colors disabled:opacity-50"
                                    title="Remove payment method"
                                  >
                                    {removingPaymentMethod === pm.id ? (
                                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <TrashIcon className="w-4 h-4" />
                                    )}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Inline Stripe PaymentElement form */}
                        {setupClientSecret ? (
                          <AddPaymentMethodForm
                            clientSecret={setupClientSecret}
                            onSuccess={handlePaymentMethodAdded}
                            onCancel={() => setSetupClientSecret(null)}
                          />
                        ) : (
                          <div className="flex gap-3 mt-4">
                            <button
                              onClick={handleAddPaymentMethod}
                              disabled={addingPaymentMethod}
                              className="btn btn-sm flex items-center gap-2 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
                            >
                              {addingPaymentMethod ? (
                                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                              ) : (
                                <PlusIcon className="w-4 h-4" />
                              )}
                              {subscription?.stripeCustomerId ? 'Add Payment Method' : 'Subscribe & Add Payment'}
                            </button>
                            {showManageOnStripe && (
                              <button
                                onClick={handleManageBilling}
                                className="btn btn-secondary btn-sm"
                              >
                                Manage on Stripe
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Cancel Subscription Confirmation Modal */}
          {showCancelConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-dark-900 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center gap-3 mb-4">
                  <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cancel Subscription?</h3>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                  Your subscription will remain active until the end of your current billing period
                  {subscription?.currentPeriodEnd && ` (${format(new Date(subscription.currentPeriodEnd), 'MMMM dd, yyyy')})`}.
                  After that, you'll be downgraded to the Free plan.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCancelConfirm(false)}
                    className="btn btn-secondary flex-1"
                    disabled={cancelingSubscription}
                  >
                    Keep Plan
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelingSubscription}
                    className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white rounded-lg font-medium transition-colors"
                  >
                    {cancelingSubscription ? 'Canceling...' : 'Cancel Subscription'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Danger Zone */}
          <div className="mt-8 border border-red-300 dark:border-red-800 rounded-xl bg-red-50/50 dark:bg-red-900/10 p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-400">Danger Zone</h3>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              Once you delete your account, all your data will be permanently removed. This action cannot be undone.
            </p>
            <button
              onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(''); }}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors"
            >
              <TrashIcon className="w-4 h-4" />
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Pre-OAuth Consent Dialog */}
      {consentPlatform && (
        <PreOAuthConsentDialog
          platform={consentPlatform}
          onAccept={handleConsentAccepted}
          onCancel={() => setConsentPlatform(null)}
        />
      )}

      {/* Delete Account Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-900 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete Account
              </h3>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              This will permanently delete your account and all associated data. This action <strong>cannot be undone</strong>.
            </p>

            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
              <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-2">The following will be deleted:</p>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                <li>Your profile and account settings</li>
                <li>All connected platform tokens</li>
                <li>Synced analytics and metrics data</li>
                <li>Uploaded content and media files</li>
                <li>Deal and brand partnership records</li>
                <li>Revenue and expense data</li>
                <li>Notification history</li>
              </ul>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                type="text"
                className="input"
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                disabled={deleting}
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary flex-1"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900/40 text-white rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
              >
                <TrashIcon className="w-4 h-4" />
                {deleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit / Manage Connection Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-900 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Manage Connection
              </h3>
              <button
                onClick={() => setEditModal(null)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-800"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Platform</span>
                <span className="font-medium text-gray-900 dark:text-white">{editModal.platform}</span>
              </div>
              {editModal.displayName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Display Name</span>
                  <span className="font-medium text-gray-900 dark:text-white">{editModal.displayName}</span>
                </div>
              )}
              {editModal.platformUsername && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Username</span>
                  <span className="font-medium text-gray-900 dark:text-white">@{editModal.platformUsername}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Status</span>
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${editModal.status === 'ACTIVE' ? 'text-green-600' : 'text-yellow-600'}`}>
                    {editModal.status}
                  </span>
                  {editModal.primary && (
                    <span className="inline-flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400">
                      <StarIconSolid className="w-3 h-3" /> Primary
                    </span>
                  )}
                </div>
              </div>
              {editModal.lastSyncAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Last Sync</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {format(new Date(editModal.lastSyncAt), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
              )}
              {editModal.tokenExpiresAt && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Token Expires</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {format(new Date(editModal.tokenExpiresAt), 'MMM dd, yyyy HH:mm')}
                  </span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setEditModal(null)}
                className="btn btn-secondary flex-1"
                disabled={submitting}
              >
                Close
              </button>
              <button
                onClick={() => handleRefreshToken(editModal)}
                className="btn flex-1 flex items-center justify-center gap-2 text-white bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 shadow-md"
                disabled={submitting}
              >
                <ArrowPathIcon className={`w-4 h-4 ${submitting ? 'animate-spin' : ''}`} />
                {submitting ? 'Refreshing...' : 'Refresh Token'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
